/**
 * utils/priceOnRequest.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE answer to "may this product be put in a basket, and what does its price
 * row say" for the whole web client, and the same answer both customer apps
 * already give (app/utils/priceOnRequest.js in Lusuku_Expo_Clean and
 * Lusuku_IOS). A shopper who opens a brand shop in Chrome and then on their
 * phone must not be told two different things about one SKU, which is exactly
 * what happened while each surface carried its own rule.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Every web surface that shows a price decided for itself, and most of them
 * decided wrongly:
 *
 *   • BrandShopPage and BrandProductPage read `product.priceHidden` directly in
 *     about a dozen places. That field is only ever set by the server's
 *     `mapProductForPublic`, so any payload that had not been through the
 *     mapper lost the posture entirely. `GET /api/brands/:id/products/:productId`
 *     and `GET /api/brands/:id/sections` used to return raw documents, which is
 *     why a cold load of a brand product page rendered "USh 0" with a live Add
 *     to cart on an item the server would never sell. The server now maps every
 *     public product read, and this module reads the mapped field first so the
 *     client cannot regress the moment a new endpoint forgets.
 *   • The "Quote only" badge tested `product.orderFlowOverride === "QUOTE"`,
 *     which is the SKU's own override and nothing else. A brand whose whole
 *     catalogue is sold by quote leaves its SKUs on `INHERIT`, so every product
 *     in the shop that most needed the badge was the one that never got it.
 *   • HealthcareShopPage and HealthcareProductDialog had no notion of the
 *     posture at all: a quote-only pharmacy line printed its raw price (or a
 *     confident zero) beside a live Add to Cart.
 *   • vendorSeo's share text printed `p.price` straight out, so a shared link
 *     said "USh 0" in the message body while the OG card attached to the very
 *     same message correctly said "Price on request".
 *   • SearchResultsPage rendered no price element at all for such a result,
 *     which looks identical to a result whose price failed to load.
 *
 * THE FIELD TO TRUST IS `effectiveOrderFlow`, which the server derives once in
 * `mapProductForPublic` and every public product read now returns. It already
 * folds in the three separate things that hide a price (the SKU's own
 * `priceOnRequest`, the vendor's `hidePricesGlobally`, and a QUOTE-tier
 * vendor), so a client that switches on it cannot know about one and miss the
 * other two, which is precisely what every hand-rolled check here used to do.
 * `priceHidden` and `priceOnRequest` are read only as fallbacks, for a payload
 * that predates the mapping: an older cached response, or a cart line that was
 * written to localStorage before this release and is still sitting there.
 *
 * THREE STATES, NOT TWO. "Has no price" and "is sold by quote" are different
 * things and the difference is the whole point:
 *
 *   quote-only   a deliberate commercial posture. The item is complete and
 *                sellable, through a conversation. Say "Price on request" and
 *                offer the quote.
 *   unpriced     nobody has typed a price yet, usually a fresh import. There is
 *                no posture behind it and no quote to ask for. Say so plainly
 *                and disable the button; the server refuses this line anyway
 *                (PRICE_UNSET), so an enabled button could only ever produce a
 *                failed checkout.
 *   priced       the ordinary case.
 *
 * So NEVER derive the posture from `price === 0`. The server has always said as
 * much: `validateBrandOrderLines` guards its price check with
 * `if (enforcePrice && !product.priceOnRequest)`, which means a quote-only
 * product is explicitly allowed to keep a non-zero price from before the vendor
 * flipped the posture. A price of zero is not a quote posture, and a quote
 * posture does not imply a price of zero.
 *
 * And never render a price for the first two states. A confident "USh 0" is
 * worse than a blank: it answers the question the item exists to make the
 * shopper ask.
 *
 * WHERE THE LINE MATHS LIVES. This module answers the POSTURE question only.
 * What a cart line is worth stays in utils/minimumOrder.js (`lineSubtotal` /
 * `linesSubtotal`), which already knows about `qty` versus `quantity`, option
 * prices and variant deltas across every vendor type. A caller that needs a
 * subtotal of the orderable lines partitions with `partitionCartLines` here and
 * hands `partition.sellable` to `linesSubtotal` there, so there is one posture
 * rule and one money rule rather than two of each.
 */

/** The one wording, so no screen invents its own. */
export const PRICE_ON_REQUEST_LABEL = "Price on request";
export const PRICE_UNAVAILABLE_LABEL = "Price unavailable";

/**
 * Is this product sold through a quote rather than at a published price?
 *
 * @param {object|null} product a product as any public read returns it, or a
 *                              cart line built from one
 * @returns {boolean}
 */
export function isQuoteOnly(product) {
  if (!product) return false;
  // The derived field, and the one to prefer: set by mapProductForPublic, and
  // the only one that accounts for a vendor-wide or tier-wide posture as well
  // as the SKU's own.
  if (product.effectiveOrderFlow === "QUOTE") return true;
  // What the mapper sets alongside it.
  if (product.priceHidden === true) return true;
  // The SKU's own posture, for a payload that has not been through the mapper
  // (an older cached response, or a line stored in a cart before this release).
  if (product.priceOnRequest === true) return true;
  return false;
}

/**
 * The number to render, or null when there is nothing honest to render.
 *
 * Returns null for a quote-only product AND for a product whose price is
 * absent, zero or unparseable, so no caller can print "USh 0". A caller that
 * gets null asks `isQuoteOnly` which of the two labels above to show.
 *
 * Reads the mapped `displayPrice` first: on a TIERED vendor that is the buyer's
 * own tier price rather than the base price, and showing a wholesaler the base
 * price is showing them the wrong number. Note that the mapper nulls
 * `displayPrice`, `price`, `listPrice`, `salePrice` and `discount` together on a
 * quote-only product, so on a mapped payload every one of those reads is empty
 * and the quote-only short-circuit below is what actually decides.
 *
 * @param {object|null} product
 * @returns {number|null}
 */
export function publishedPrice(product) {
  if (!product || isQuoteOnly(product)) return null;
  const candidates = [product.displayPrice, product.price];
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate === undefined || candidate === null) continue;
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

/**
 * May this product go into a basket?
 *
 * The client-side twin of the server's own line check. It exists to give a
 * shopper a good affordance, NOT to keep the data honest: the server refuses
 * both of these lines whatever a client believes, which is the only reason it
 * is safe for this to be a one-line read.
 *
 * @param {object|null} product
 * @returns {boolean}
 */
export function canAddToCart(product) {
  return !!product && !isQuoteOnly(product) && publishedPrice(product) !== null;
}

/**
 * The price row's text, for the screens that render one string rather than a
 * struck-through pair. `format` is the caller's own currency formatter, so a
 * Zambian shopper sees ZMW where a Ugandan sees USh.
 *
 * @param {object|null} product
 * @param {(n:number)=>string} format
 * @returns {string}
 */
export function priceLabel(product, format) {
  const value = publishedPrice(product);
  const render = typeof format === "function" ? format : String;
  if (value !== null) return render(value);
  return isQuoteOnly(product) ? PRICE_ON_REQUEST_LABEL : PRICE_UNAVAILABLE_LABEL;
}

/**
 * Why this cart line cannot be ordered, or null when it can be.
 *
 * The two answers are the two the shopper needs different words and different
 * controls for, and the same two the server refuses under different codes:
 *
 *   "QUOTE"     a deliberate posture. There IS a price, through a conversation,
 *               so point them at the quote (server: PRICE_ON_REQUEST).
 *   "UNPRICED"  nobody has costed this yet. There is no quote to ask for, so
 *               the only honest move is to take it out of the cart
 *               (server: PRICE_UNSET).
 *
 * Deliberately reads the line's OWN price and ignores its options and variant
 * delta: those are add-ons to a base price, not a substitute for one, so an
 * item with no price of its own is unpriced however many paid options are
 * ticked on it.
 *
 * @param {object|null} line
 * @returns {"QUOTE"|"UNPRICED"|null}
 */
export function cartLineProblem(line) {
  if (!line) return null;
  if (isQuoteOnly(line)) return "QUOTE";
  return publishedPrice(line) !== null ? null : "UNPRICED";
}

/**
 * Split a cart's lines into what may be ordered and what may not.
 *
 * Carts need the split, not a boolean. A cart with SOME quote-only lines can
 * still be checked out once they are removed, and its minimum-order floor has
 * to be measured against the priced lines alone, while a cart with NOTHING but
 * quote-only lines cannot be checked out at all and must say so instead of
 * quoting a shortfall against a subtotal of zero. That second case was live on
 * web: `minimumOrderShortfall` returns 0 for a subtotal of 0 (by design, so an
 * empty cart is not nagged), so a cart holding only price-nulled lines cleared
 * the floor, enabled Proceed to Checkout and sent the shopper to a checkout
 * that the server refuses.
 *
 * @param {Array<object>} items
 * @returns {{sellable: Array, quoteOnly: Array, unpriced: Array,
 *            unsellable: Array, allUnsellable: boolean}}
 */
export function partitionCartLines(items) {
  const lines = Array.isArray(items) ? items : [];
  const sellable = [];
  const quoteOnly = [];
  const unpriced = [];
  lines.forEach((line) => {
    const problem = cartLineProblem(line);
    if (problem === "QUOTE") quoteOnly.push(line);
    else if (problem === "UNPRICED") unpriced.push(line);
    else sellable.push(line);
  });
  return {
    sellable,
    quoteOnly,
    unpriced,
    // Quote-only first, so a notice that can only name one line names the one
    // with a way forward.
    unsellable: quoteOnly.concat(unpriced),
    // An EMPTY cart is not "all unsellable": the carts' own empty state already
    // says the cart is empty, and this must not speak over it.
    allUnsellable: lines.length > 0 && sellable.length === 0,
  };
}

/**
 * The sentence a cart shows about its unorderable lines, or null when it has
 * none, so a caller can render it unconditionally.
 *
 * ONE wording, used both as the notice in the cart footer and as the label on
 * the disabled checkout button: pressing through a blocker must never
 * introduce a second account of one rule. It deliberately does NOT promise a
 * quote after checkout, because the server refuses the order outright, so that
 * promise could only ever end in a failed order with the line still in the cart.
 *
 * @param {{unsellable: Array, allUnsellable: boolean}} partition
 * @returns {string|null}
 */
export function unsellableLinesMessage(partition) {
  const count = Array.isArray(partition && partition.unsellable)
    ? partition.unsellable.length
    : 0;
  if (count <= 0) return null;
  if (partition.allUnsellable) {
    return count === 1
      ? "This item has to be quoted by the vendor before it can be ordered. Remove it, or request a quote from the shop."
      : "These items have to be quoted by the vendor before they can be ordered. Remove them, or request a quote from the shop.";
  }
  return count === 1
    ? "One item has to be quoted first. Remove it to order the rest."
    : `${count} items have to be quoted first. Remove them to order the rest.`;
}

/**
 * THE SERVER'S TWO REFUSALS for a line it cannot charge for, named once so no
 * screen has to spell either string out again. Order-create answers HTTP 400
 * PRICE_ON_REQUEST for a quote-only line and HTTP 400 PRICE_UNSET for a line
 * whose chargeable price is not above zero, each with a `message` and usually a
 * `violations` array of { code, productId, name, message }.
 */
export const PRICE_ON_REQUEST_CODE = "PRICE_ON_REQUEST";
export const PRICE_UNSET_CODE = "PRICE_UNSET";

const UNSELLABLE_CODES = [PRICE_ON_REQUEST_CODE, PRICE_UNSET_CODE];

/**
 * The body of a refusal, whichever shape the caller happens to hold.
 *
 * An axios rejection carries the body at `err.response.data`, a rethrown or
 * normalised failure carries it at `err.data`, and a redux thunk that called
 * `rejectWithValue` hands the screen the body itself. A screen should not have
 * to know which one it is holding.
 */
function refusalBody(source) {
  if (!source || typeof source !== "object") return null;
  return (source.response && source.response.data) || source.data || source;
}

function refusalViolations(body) {
  return Array.isArray(body && body.violations) ? body.violations : [];
}

/**
 * True when this response body (or error) is the server refusing a line that
 * cannot be charged for, under either code, at the top level or inside
 * `violations`.
 *
 * @param {object|null} source a response body, or an error holding one
 * @returns {boolean}
 */
export function isUnsellableLineRefusal(source) {
  const body = refusalBody(source);
  if (!body) return false;
  if (UNSELLABLE_CODES.indexOf(body.code) !== -1) return true;
  return refusalViolations(body).some(
    (violation) => violation && UNSELLABLE_CODES.indexOf(violation.code) !== -1
  );
}

/**
 * Turn that refusal into something the shopper can act on.
 *
 * WHY the server's own `message` is carried through rather than replaced: it is
 * written against the ACTUAL state of the catalogue at the moment of the POST,
 * which a checkout tab that has been open for an hour may no longer know. The
 * `violations` array then names each offending product, which is what lets a
 * dialog remove exactly those lines instead of clearing the whole cart.
 * `productIds` is empty when the server named nobody, and a caller that gets
 * nothing falls back to its own detection.
 *
 * @param {object|null} source a response body, or an error holding one
 * @returns {{title: string, message: string, productIds: string[],
 *            names: string[], count: number}}
 */
export function describeUnsellableLineRefusal(source) {
  const body = refusalBody(source) || {};
  const violations = refusalViolations(body).filter(
    (violation) => violation && UNSELLABLE_CODES.indexOf(violation.code) !== -1
  );
  const names = violations
    .map((violation) => String(violation.name || "").trim())
    .filter(Boolean);
  const productIds = violations
    .map((violation) =>
      violation.productId == null ? "" : String(violation.productId)
    )
    .filter(Boolean);
  const count = violations.length || 1;

  const serverMessage = String(body.message || "").trim();
  const parts = [];
  if (serverMessage) parts.push(serverMessage);
  /* The names go in only when they ADD something. The server's sentence usually
     names the product itself when there is one of them ("Chai Bora 500g is sold
     on request"), and repeating it underneath reads like a second, different
     problem. */
  const unsaid = names.filter((name) => serverMessage.indexOf(name) === -1);
  if (unsaid.length) parts.push(unsaid.join(", "));
  if (!parts.length) {
    parts.push(
      count > 1
        ? "These items have to be quoted by the vendor before they can be ordered."
        : "This item has to be quoted by the vendor before it can be ordered."
    );
  }

  return {
    title: count > 1 ? "Some items need a quote" : "This item needs a quote",
    message: parts.join(" "),
    productIds,
    names,
    count,
  };
}

/* ───────────────────────────────────────────────────────────────────────────
   WHERE THE BUYER'S QUOTE HAS GOT TO

   Founder rule (29 Sep 2026): price on request is an interactive flow and the
   progress of that interaction has to be visible until the sale concludes.
   The lifecycle has always existed server-side (REQUEST_LIFECYCLE in
   services/brandQuoteLifecycleService.js) and the My Quotes screen has always
   rendered it, but the product a shopper actually asked about showed none of
   it: a buyer who had already requested a quote, and was waiting, came back to
   the same "Request a quote" button as someone who had never asked. The honest
   answer to "what happened to my request" was on a different screen.

   These descriptions live beside the price rules rather than in a screen so
   that the app, the web client and any surface added later say the same
   sentence about the same state. The wording is written for the BUYER, which is
   why it names the vendor and never the lifecycle constant: "AWAITING_VENDOR"
   is a fact about our database, not an answer to their question.
   ─────────────────────────────────────────────────────────────────────────── */

/** The lifecycles in which a request is still live, so no second one is needed. */
export const OPEN_QUOTE_LIFECYCLES = [
  "AWAITING_VENDOR",
  "QUOTED",
  "NEGOTIATING",
];

/**
 * Read a request's lifecycle exactly as the server's `getRequestLifecycle`
 * reads it, legacy rows included, so a client and the server can never disagree
 * about the same request.
 */
export function quoteLifecycleOf(request) {
  if (request?.lifecycleStatus) return request.lifecycleStatus;
  if (request?.status === "CANCELLED") return "CANCELLED";
  if (request?.status === "CLOSED") return "QUOTED";
  return request ? "AWAITING_VENDOR" : null;
}

/**
 * The buyer's own request for one product, or null.
 *
 * Prefers a live one over a finished one, because a buyer who asked, was
 * declined, and asked again should see the second conversation rather than the
 * first. The caller passes the list newest first (the API sorts that way), so
 * the first live match is also the most recent.
 */
export function liveQuoteFor(requests) {
  const list = Array.isArray(requests) ? requests : [];
  const live = list.find((r) => OPEN_QUOTE_LIFECYCLES.includes(quoteLifecycleOf(r)));
  return live || list[0] || null;
}

/**
 * What to tell the buyer, and what the button should now do.
 *
 * @param {object|null} request the buyer's own request, from liveQuoteFor
 * @param {string} [vendorName] shown instead of "the vendor" when known
 * @returns {{state:string, live:boolean, title:string, detail:string, action:string}|null}
 *          null when there is no request, which is when the ordinary
 *          "Request a quote" affordance belongs.
 */
export function describeQuoteProgress(request, vendorName) {
  const lifecycle = quoteLifecycleOf(request);
  if (!lifecycle) return null;
  const who = vendorName || "the vendor";

  const BY_STATE = {
    AWAITING_VENDOR: {
      live: true,
      title: "Quote requested",
      detail: `Waiting for ${who} to send you a price.`,
      action: "View my request",
    },
    QUOTED: {
      live: true,
      title: "Quote received",
      detail: `${who} has sent you a price. Accept it, counter it, or ask for a revision.`,
      action: "View the quote",
    },
    NEGOTIATING: {
      live: true,
      title: "Your reply is with the vendor",
      detail: `Waiting for ${who} to come back on your last message.`,
      action: "View the conversation",
    },
    ACCEPTED: {
      live: false,
      title: "Quote accepted",
      detail: "Your order was created from this quote.",
      action: "View the order",
    },
    FULFILLED: {
      live: false,
      title: "Completed",
      detail: "This quote has been fulfilled.",
      action: "View it",
    },
    DECLINED: {
      live: false,
      title: "Quote declined",
      detail: "You can ask again if you still need a price.",
      action: "Ask again",
    },
    CANCELLED: {
      live: false,
      title: "Request cancelled",
      detail: "You can ask again if you still need a price.",
      action: "Ask again",
    },
    EXPIRED: {
      live: false,
      title: "Quote expired",
      detail: "You can ask again if you still need a price.",
      action: "Ask again",
    },
  };

  const meta = BY_STATE[lifecycle];
  if (!meta) return null;
  return { state: lifecycle, ...meta };
}

/**
 * May the buyer start a NEW request for this product?
 *
 * False while one is live, because two open requests for the same product give
 * the vendor two things to answer and the buyer two places to look.
 */
export function canRequestQuote(request) {
  const progress = describeQuoteProgress(request);
  return !progress || progress.live !== true;
}

const priceOnRequest = {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  PRICE_ON_REQUEST_CODE,
  PRICE_UNSET_CODE,
  isQuoteOnly,
  publishedPrice,
  canAddToCart,
  priceLabel,
  cartLineProblem,
  partitionCartLines,
  unsellableLinesMessage,
  isUnsellableLineRefusal,
  describeUnsellableLineRefusal,
  OPEN_QUOTE_LIFECYCLES,
  quoteLifecycleOf,
  liveQuoteFor,
  describeQuoteProgress,
  canRequestQuote,
};

export default priceOnRequest;
